import { useCallback } from 'react';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { VerificationRequiredActions } from '../../publicSession/types';
import { useVerification } from '../../VerificationPopup/withVerification';
import { useRestrictToLoggedIn } from './useRestrictToLoggedIn';
import noop from '../noop';

interface ReturnType {
  isLoggedIn: boolean;
  isVerificationNeeded: boolean;
  restrictToLoggedInAndVerified: (
    restrictedActionName: ReferralType,
    actionType?: VerificationRequiredActions,
    callback?: (ev?: any) => void
  ) => any;
}

/** returns whether current user:
 *    - is logged in
 *    - has verfified their email
 *  and a function that will:
 *    - send the user to the join page if they're logged out
 *    - open the verification popup if they haven't verified
 *    - call the given callback if logged in and verified
 */
export function useRestrictToLoggedInAndVerified(): ReturnType {
  const { isLoggedIn, sendUserToJoinPage } = useRestrictToLoggedIn();
  const { isVerificationNeeded, pushVerificationPopup } = useVerification();

  const restrictToLoggedInAndVerified = useCallback(
    (
      restrictedActionName: ReferralType,
      actionType?: VerificationRequiredActions,
      callback: (ev?: any) => void = noop
    ) =>
      (ev?: MouseEvent) => {
        if (!isLoggedIn) {
          ev?.preventDefault && ev.preventDefault();
          return sendUserToJoinPage(restrictedActionName);
        }

        if (isVerificationNeeded) {
          ev?.preventDefault && ev.preventDefault();
          return pushVerificationPopup(actionType);
        }

        return callback(ev);
      },
    [
      isLoggedIn,
      sendUserToJoinPage,
      isVerificationNeeded,
      pushVerificationPopup,
    ]
  );

  return { isLoggedIn, isVerificationNeeded, restrictToLoggedInAndVerified };
}
